'use client'

import { ChangeEvent, Dispatch, FocusEvent, SetStateAction } from 'react'
import { Cell, Checkbox, InputText, Text } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  ShippingContactValidationModel,
  FieldName,
  ShippingContactType,
} from '@marketplace-web/shipping/shipping-contact-data'

import {
  checkoutBuyerInputEvent,
  transactionSellerInputEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

type Props = {
  transactionId: number | null
  validations?: Array<ShippingContactValidationModel>
  value: string | null | undefined
  persist: boolean
  isCompleted: boolean
  type: ShippingContactType
  error: string | null | undefined
  isLoading: boolean
  setError: Dispatch<SetStateAction<string | null | undefined>>
  onBlur: (value: string) => void
  onPersist: () => void
  onInputChange: Dispatch<SetStateAction<string | null>>
}

const PhoneNumber = ({
  transactionId,
  validations,
  value,
  persist,
  isCompleted,
  type,
  error,
  isLoading,
  setError,
  onBlur,
  onPersist,
  onInputChange,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('shipping_contact.seller.form.phone_number')

  const fieldName =
    type === ShippingContactType.Buyer ? FieldName.BuyerPhoneNumber : FieldName.SellerPhoneNumber

  const trackBlur = (isValid: boolean, trackingTarget?: string) => {
    if (!transactionId) return

    if (type === ShippingContactType.Buyer) {
      track(
        checkoutBuyerInputEvent({
          screen: 'contact_details',
          target: 'phone_number',
          transactionId,
          valid: isValid,
          targetDetails: trackingTarget,
        }),
      )

      return
    }

    track(
      transactionSellerInputEvent({
        screen: 'seller_contact_details',
        target: 'save_phone_number',
        valid: isValid,
        transactionId,
        targetDetails: trackingTarget,
      }),
    )
  }

  const getFirstFailedValidation = (inputValue: string) => {
    return validations?.find(({ regex }) => !new RegExp(regex).test(inputValue))
  }

  const handleFailedValidation = (failedValidation: ShippingContactValidationModel) => {
    const { trackingTarget, message } = failedValidation

    trackBlur(false, trackingTarget)

    setError(message)
  }

  const handleValidInput = (inputValue: string) => {
    trackBlur(true)
    onBlur(inputValue)

    if (!error) return

    setError(null)
  }

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const failedValidation = getFirstFailedValidation(event.target.value)

    if (failedValidation) {
      handleFailedValidation(failedValidation)

      return
    }

    handleValidInput(event.target.value)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onInputChange(event.target.value)
  }

  const renderPersistenceCheck = () => (
    <Cell
      prefix={
        <Checkbox
          testId="save-for-later"
          name={`${fieldName}_persist`}
          onClick={onPersist}
          disabled={isLoading}
          checked={persist}
          onChange={() => undefined}
        />
      }
      body={isCompleted ? translate('save_for_later.update') : translate('save_for_later.add')}
    />
  )

  return (
    <>
      <InputText
        name={fieldName}
        title={translate('title')}
        placeholder={translate('placeholder')}
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        validation={error && <Text as="h4" text={error} type={Text.Type.Caption} theme="warning" />}
      />
      {renderPersistenceCheck()}
    </>
  )
}

export default PhoneNumber
